import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { apiFetch } from '../services/apiUtils';

type LeadTypeFilter = 'ALL' | 'MessageLead' | 'WhatsAppLead';
type EmailStatusFilter = 'ALL' | 'HAS_EMAIL' | 'MISSING_EMAIL';
type ActionTone = 'info' | 'success' | 'warning' | 'error';
type PortalSource = 'PRIVATE_PROPERTY' | 'PROPERTY24';

interface LeadRow {
  id: string;
  lead_id: string;
  lead_type: string | null;
  lead_name: string | null;
  lead_email: string | null;
  lead_phone_number: string | null;
  lead_message: string | null;
  listing_reference: string | null;
  listing_external_reference: string | null;
  listing_type: string | null;
  listing_url: string | null;
  listing_address: string | null;
  listing_suburb: string | null;
  agent_id: string | null;
  agent_name: string | null;
  agent_email: string | null;
  market_center_name: string | null;
  agency_name: string | null;
  agency_account_number: string | null;
  price: string | null;
  price_type: string | null;
  lead_date_time: string | null;
  lead_sent_date_time: string | null;
  received_at: string;
}

interface LeadsResponse {
  items: LeadRow[];
  total: number;
  limit: number;
  offset: number;
}

interface Property24SyncStatus {
  enabled: boolean;
  autoSyncEnabled: boolean;
  running: boolean;
  lastAfter: string | null;
  lastSyncAt: string | null;
  lastSyncError: string | null;
  pollIntervalSeconds: number;
}

const PAGE_SIZE = 50;

function formatDate(value: string | null): string {
  if (!value) return '-';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString('en-ZA', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function compactText(value: string | null, max = 120): string {
  if (!value) return '-';
  const trimmed = value.trim();
  if (trimmed.length <= max) return trimmed;
  return `${trimmed.slice(0, max)}...`;
}

function formatPrice(value: string | null, priceType: string | null): string {
  if (!value) return '-';
  const amount = Number(value);
  const rendered = Number.isFinite(amount)
    ? amount.toLocaleString('en-ZA', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    : value;
  return priceType ? `${rendered} (${priceType})` : rendered;
}

function getEndpointForPortal(portal: PortalSource): string {
  return portal === 'PRIVATE_PROPERTY' ? '/api/private-property-leads' : '/api/property24-leads';
}

function getPortalLabel(portal: PortalSource): string {
  return portal === 'PRIVATE_PROPERTY' ? 'Private Property' : 'Property24';
}

export default function PortalLeadsPage() {
  const { token, activeContext, isRegionalAdmin } = useAuth();
  const [portal, setPortal] = useState<PortalSource>('PRIVATE_PROPERTY');
  const [search, setSearch] = useState('');
  const [leadType, setLeadType] = useState<LeadTypeFilter>('ALL');
  const [emailStatus, setEmailStatus] = useState<EmailStatusFilter>('ALL');
  const [marketCenter, setMarketCenter] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [page, setPage] = useState(1);
  const [sharingLeadId, setSharingLeadId] = useState<string | null>(null);
  const [actionMessage, setActionMessage] = useState<string | null>(null);
  const [actionTone, setActionTone] = useState<ActionTone>('info');
  const [syncingP24, setSyncingP24] = useState(false);

  const authHeaders = {
    Authorization: `Bearer ${token ?? ''}`,
    'X-Active-Context': activeContext?.id ?? '',
  };

  const queryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set('limit', String(PAGE_SIZE));
    params.set('offset', String((page - 1) * PAGE_SIZE));
    if (search.trim()) params.set('search', search.trim());
    if (portal === 'PRIVATE_PROPERTY' && leadType !== 'ALL') params.set('leadType', leadType);
    if (emailStatus !== 'ALL') params.set('emailStatus', emailStatus);
    if (marketCenter.trim()) params.set('marketCenter', marketCenter.trim());
    if (fromDate) params.set('fromDate', fromDate);
    if (toDate) params.set('toDate', toDate);
    return params.toString();
  }, [page, search, portal, leadType, emailStatus, marketCenter, fromDate, toDate]);

  const { data: p24SyncStatus } = useQuery({
    queryKey: ['portal-leads', 'property24-sync-status', token, activeContext?.id],
    queryFn: async () => {
      const response = await apiFetch('/api/property24-leads/sync-status', { headers: authHeaders });
      const body = await response.json().catch(() => ({})) as Partial<Property24SyncStatus> & { error?: string };
      if (!response.ok) {
        throw new Error(body.error ?? 'Failed to load Property24 sync status.');
      }
      return {
        enabled: Boolean(body.enabled),
        autoSyncEnabled: Boolean(body.autoSyncEnabled),
        running: Boolean(body.running),
        lastAfter: body.lastAfter ?? null,
        lastSyncAt: body.lastSyncAt ?? null,
        lastSyncError: body.lastSyncError ?? null,
        pollIntervalSeconds: Number(body.pollIntervalSeconds ?? 0),
      } as Property24SyncStatus;
    },
    enabled: Boolean(token) && Boolean(isRegionalAdmin),
    staleTime: 30_000,
  });

  const property24Disabled = portal === 'PROPERTY24' && p24SyncStatus?.enabled === false;

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['portal-leads', portal, token, activeContext?.id, queryString],
    queryFn: async () => {
      if (property24Disabled) {
        return {
          items: [],
          total: 0,
          limit: PAGE_SIZE,
          offset: 0,
        } as LeadsResponse;
      }
      const response = await apiFetch(`${getEndpointForPortal(portal)}?${queryString}`, { headers: authHeaders });
      const body = await response.json().catch(() => ({})) as Partial<LeadsResponse> & { error?: string };
      if (!response.ok) {
        throw new Error(body.error ?? `Failed to load ${getPortalLabel(portal)} leads.`);
      }
      return {
        items: body.items ?? [],
        total: body.total ?? 0,
        limit: body.limit ?? PAGE_SIZE,
        offset: body.offset ?? 0,
      } as LeadsResponse;
    },
    enabled: Boolean(token) && Boolean(isRegionalAdmin),
  });

  async function syncProperty24Now(): Promise<void> {
    if (property24Disabled) {
      setActionTone('warning');
      setActionMessage('Property24 leads are disabled. Set PROPERTY24_LEADS_ENABLED=true to enable.');
      return;
    }

    setActionMessage(null);
    setActionTone('info');
    setSyncingP24(true);
    try {
      const response = await apiFetch('/api/property24-leads/sync-now', {
        method: 'POST',
        headers: authHeaders,
      });
      const body = await response.json().catch(() => ({})) as { error?: string; insertedOrUpdated?: number };
      if (!response.ok) {
        throw new Error(body.error ?? 'Failed to sync Property24 leads.');
      }
      setActionTone('success');
      setActionMessage(`Property24 sync completed. Rows processed: ${body.insertedOrUpdated ?? 0}.`);
      await refetch();
    } catch (syncError) {
      setActionTone('error');
      setActionMessage((syncError as Error).message);
    } finally {
      setSyncingP24(false);
    }
  }

  async function shareLead(row: LeadRow): Promise<void> {
    if (portal !== 'PRIVATE_PROPERTY') {
      return;
    }

    setActionMessage(null);
    setActionTone('info');

    const resolvedEmail = row.agent_email?.trim() ?? '';
    let recipientEmail: string | undefined;
    if (!resolvedEmail) {
      const entered = window.prompt(
        `No agent email is linked for lead ${row.lead_id}. Enter recipient email to continue:`,
      );
      if (!entered || !entered.trim()) {
        setActionTone('warning');
        setActionMessage('Share cancelled. Agent email not found; no recipient was entered.');
        return;
      }
      recipientEmail = entered.trim();
    }

    const targetRecipient = recipientEmail ?? resolvedEmail;
    const proceed = window.confirm(
      `Warning: this will email lead ${row.lead_id} to ${targetRecipient}. Continue?`,
    );
    if (!proceed) {
      setActionTone('warning');
      setActionMessage('Share cancelled. No email was sent.');
      return;
    }

    setSharingLeadId(row.id);
    try {
      const response = await apiFetch('/api/private-property-leads/notify-agent', {
        method: 'POST',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ leadRowId: row.id, ...(recipientEmail ? { recipientEmail } : {}) }),
      });
      const body = await response.json().catch(() => ({})) as { error?: string; recipient?: string };
      if (!response.ok) {
        throw new Error(body.error ?? 'Failed to share lead with agent.');
      }
      setActionTone('success');
      setActionMessage(`Lead ${row.lead_id} shared to ${body.recipient ?? 'agent email'}.`);
    } catch (shareError) {
      const message = (shareError as Error).message;
      setActionTone(/not found|provide recipientemail manually/i.test(message) ? 'warning' : 'error');
      setActionMessage(message);
    } finally {
      setSharingLeadId(null);
    }
  }

  function resetFilters(): void {
    setPage(1);
    setSearch('');
    setLeadType('ALL');
    setEmailStatus('ALL');
    setMarketCenter('');
    setFromDate('');
    setToDate('');
  }

  function switchPortal(nextPortal: PortalSource): void {
    if (nextPortal === portal) return;
    setPortal(nextPortal);
    setPage(1);
    setActionMessage(null);
    setActionTone('info');
  }

  if (!isRegionalAdmin) {
    return (
      <div className="surface-card p-5">
        <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
          This view is only available in Regional Admin context.
        </p>
      </div>
    );
  }

  const rows = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const errorMessage = error ? (error as Error).message : null;
  const showErrorMessage = Boolean(errorMessage) && errorMessage !== actionMessage;

  return (
    <div className="space-y-4">
      <header className="surface-card p-5">
        <div className="flex flex-col gap-3">
          <div className="flex flex-col gap-2 md:flex-row md:items-end md:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900">Portal Leads</h1>
              <p className="text-sm text-slate-600">Central lead intake for Private Property and Property24. Regional Admin only.</p>
            </div>
            <div className="text-sm text-slate-600">
              Total {getPortalLabel(portal)} leads: <span className="font-semibold text-slate-900">{total}</span>
            </div>
          </div>

          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1">
            <button
              type="button"
              onClick={() => switchPortal('PRIVATE_PROPERTY')}
              className={[
                'rounded-md px-4 py-2 text-sm font-semibold transition',
                portal === 'PRIVATE_PROPERTY' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
              ].join(' ')}
            >
              Private Property
            </button>
            <button
              type="button"
              onClick={() => switchPortal('PROPERTY24')}
              className={[
                'rounded-md px-4 py-2 text-sm font-semibold transition',
                portal === 'PROPERTY24' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900',
              ].join(' ')}
            >
              Property24
            </button>
          </div>
        </div>
      </header>

      <section className="surface-card p-5">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-6">
          <label className="md:col-span-2">
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Search</span>
            <input
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
              placeholder="Lead, agent, market centre, suburb, listing ref"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>

          {portal === 'PRIVATE_PROPERTY' ? (
            <label>
              <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Lead Type</span>
              <select
                value={leadType}
                onChange={(event) => {
                  setPage(1);
                  setLeadType(event.target.value as LeadTypeFilter);
                }}
                className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="ALL">All</option>
                <option value="MessageLead">MessageLead</option>
                <option value="WhatsAppLead">WhatsAppLead</option>
              </select>
            </label>
          ) : (
            <div />
          )}

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Agent Email</span>
            <select
              value={emailStatus}
              onChange={(event) => {
                setPage(1);
                setEmailStatus(event.target.value as EmailStatusFilter);
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="ALL">All</option>
              <option value="HAS_EMAIL">Has Email</option>
              <option value="MISSING_EMAIL">Missing Email</option>
            </select>
          </label>

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">Market Centre</span>
            <input
              value={marketCenter}
              onChange={(event) => {
                setPage(1);
                setMarketCenter(event.target.value);
              }}
              placeholder="Filter by market centre"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">From</span>
            <input
              type="date"
              value={fromDate}
              onChange={(event) => {
                setPage(1);
                setFromDate(event.target.value);
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>

          <label>
            <span className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-500">To</span>
            <input
              type="date"
              value={toDate}
              onChange={(event) => {
                setPage(1);
                setToDate(event.target.value);
              }}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>

          <div className="flex items-end">
            <button
              type="button"
              onClick={resetFilters}
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Reset Filters
            </button>
          </div>
        </div>

        {portal === 'PROPERTY24' && (
          <div className="mt-3 flex justify-end">
            <button
              type="button"
              onClick={() => void syncProperty24Now()}
              disabled={syncingP24 || property24Disabled}
              className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-sm font-semibold text-sky-800 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {syncingP24 ? 'Syncing Property24...' : 'Sync Property24 Now'}
            </button>
          </div>
        )}
      </section>

      <section className="surface-card overflow-hidden">
        {actionMessage && (
          <div
            className={[
              'border-b px-4 py-3 text-sm',
              actionTone === 'success' ? 'border-emerald-200 bg-emerald-50 text-emerald-800' : '',
              actionTone === 'warning' ? 'border-amber-200 bg-amber-50 text-amber-800' : '',
              actionTone === 'error' ? 'border-rose-200 bg-rose-50 text-rose-700' : '',
              actionTone === 'info' ? 'border-slate-200 bg-slate-50 text-slate-700' : '',
            ].join(' ')}
          >
            {actionMessage}
          </div>
        )}
        {property24Disabled && !actionMessage && (
          <div className="border-b border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            Property24 leads are disabled. Set PROPERTY24_LEADS_ENABLED=true to enable.
          </div>
        )}
        {isLoading ? (
          <div className="p-6 text-sm text-slate-600">Loading leads...</div>
        ) : showErrorMessage ? (
          <div className="p-6 text-sm text-rose-700">{errorMessage}</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-slate-600">No leads found for the selected filters.</div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                  <tr>
                    <th className="px-3 py-3 text-left">Lead Date</th>
                    <th className="px-3 py-3 text-left">Lead</th>
                    <th className="px-3 py-3 text-left">Contact</th>
                    <th className="px-3 py-3 text-left">Lead Type</th>
                    <th className="px-3 py-3 text-left">Listing</th>
                    <th className="px-3 py-3 text-left">Agent</th>
                    <th className="px-3 py-3 text-left">Market Centre</th>
                    <th className="px-3 py-3 text-left">Message</th>
                    <th className="px-3 py-3 text-left">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white text-slate-800">
                  {rows.map((row) => (
                    <tr key={row.id}>
                      <td className="px-3 py-3 align-top whitespace-nowrap">
                        <div>{formatDate(row.lead_date_time ?? row.received_at)}</div>
                        <div className="text-xs text-slate-500">Sent: {formatDate(row.lead_sent_date_time)}</div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div className="font-medium text-slate-900">{row.lead_name ?? '-'}</div>
                        <div className="text-xs text-slate-500">Lead ID: {row.lead_id}</div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div>{row.lead_email ?? '-'}</div>
                        <div className="text-xs text-slate-500">{row.lead_phone_number ?? '-'}</div>
                      </td>
                      <td className="px-3 py-3 align-top whitespace-nowrap">{row.lead_type ?? '-'}</td>
                      <td className="px-3 py-3 align-top">
                        <div>{row.listing_reference ?? '-'}</div>
                        <div className="text-xs text-slate-500">External: {row.listing_external_reference ?? '-'}</div>
                        <div className="text-xs text-slate-500">Type: {row.listing_type ?? '-'}</div>
                        <div className="text-xs text-slate-500">Suburb: {row.listing_suburb ?? '-'}</div>
                        <div className="text-xs text-slate-500">Price: {formatPrice(row.price, row.price_type)}</div>
                        {row.listing_url && (
                          <a
                            href={row.listing_url}
                            target="_blank"
                            rel="noreferrer"
                            className="text-xs font-medium text-sky-700 hover:underline"
                          >
                            Open Listing
                          </a>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div>{row.agent_name ?? '-'}</div>
                        <div className="text-xs text-slate-500">Agent ID: {row.agent_id ?? '-'}</div>
                        <div className="text-xs text-slate-500">Email: {row.agent_email ?? '-'}</div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <div>{row.market_center_name ?? row.agency_name ?? '-'}</div>
                        {row.agency_name && row.agency_name !== row.market_center_name && (
                          <div className="text-xs text-slate-500">PP Agency: {row.agency_name}</div>
                        )}
                        {row.agency_account_number && (
                          <div className="text-xs text-slate-500">Account: {row.agency_account_number}</div>
                        )}
                      </td>
                      <td className="px-3 py-3 align-top">{compactText(row.lead_message)}</td>
                      <td className="px-3 py-3 align-top whitespace-nowrap">
                        {portal === 'PRIVATE_PROPERTY' ? (
                          <button
                            type="button"
                            onClick={() => void shareLead(row)}
                            disabled={sharingLeadId === row.id}
                            className="rounded border border-slate-300 px-3 py-1.5 text-xs font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            {sharingLeadId === row.id ? 'Sharing...' : 'Share to Agent'}
                          </button>
                        ) : (
                          <span className="text-xs text-slate-500">N/A</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between border-t border-slate-200 px-4 py-3 text-sm text-slate-600">
              <div>
                Showing {Math.min(total, (page - 1) * PAGE_SIZE + 1)}-{Math.min(total, page * PAGE_SIZE)} of {total}
                {isFetching && <span className="ml-2 text-slate-500">Refreshing...</span>}
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                  disabled={page <= 1}
                  className="rounded border border-slate-300 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Previous
                </button>
                <span>Page {page} / {totalPages}</span>
                <button
                  type="button"
                  onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                  disabled={page >= totalPages}
                  className="rounded border border-slate-300 px-3 py-1.5 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </>
        )}
      </section>
    </div>
  );
}
